import React from 'react';
import ReactDOM from 'react-dom';
import { FiCheckCircle, FiAlertCircle, FiAlertTriangle, FiInfo, FiX } from 'react-icons/fi';

const ICON_CFG = {
  success: { Icon: FiCheckCircle,   color: '#16a34a', bg: '#f0fdf4' },
  error:   { Icon: FiAlertCircle,   color: '#dc2626', bg: '#fef2f2' },
  warning: { Icon: FiAlertTriangle, color: '#d97706', bg: '#fffbeb' },
  info:    { Icon: FiInfo,          color: '#2563eb', bg: '#eff6ff' },
};

const BTN_CFG = {
  success: { bg: '#16a34a', hover: '#15803d' },
  error:   { bg: '#dc2626', hover: '#b91c1c' },
  warning: { bg: '#d97706', hover: '#b45309' },
  info:    { bg: '#2563eb', hover: '#1d4ed8' },
  default: { bg: '#1e3a4c', hover: '#162e3c' },
};

// ── Toast ─────────────────────────────────────────────────────────────────────

function Toast({ text, icon = 'info', dangerMode, onDismiss }) {
  const key = (dangerMode || icon === 'error') ? 'error' : (ICON_CFG[icon] ? icon : 'info');
  const color = ICON_CFG[key].color;
  const [leaving, setLeaving] = React.useState(false);

  const dismiss = React.useCallback(() => {
    setLeaving(true);
    setTimeout(onDismiss, 200);
  }, [onDismiss]);

  React.useEffect(() => {
    const t = setTimeout(dismiss, 4000);
    return () => clearTimeout(t);
  }, [dismiss]);

  return (
    <div style={{
      position: 'fixed', top: 20, right: 20, zIndex: 99999,
      background: '#fff',
      border: '1px solid #e5e7eb',
      borderRadius: 10,
      boxShadow: '0 4px 24px rgba(0,0,0,0.09), 0 1px 4px rgba(0,0,0,0.05)',
      maxWidth: 320, minWidth: 220,
      overflow: 'hidden',
      fontFamily: "'AlteHaasGroteskRegular','Segoe UI',sans-serif",
      animation: leaving ? 'ge-out 0.18s ease forwards' : 'ge-in 0.18s ease',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', padding: '12px 10px 11px 14px', gap: 10 }}>
        <p style={{ margin: 0, flex: 1, fontSize: 13.5, color: '#111827', lineHeight: 1.55 }}>{text}</p>
        <button
          onClick={dismiss}
          style={{
            background: 'none', border: 'none', cursor: 'pointer',
            padding: 3, color: '#9ca3af', display: 'flex', flexShrink: 0,
            borderRadius: 4, transition: 'color 0.15s',
          }}
          onMouseEnter={e => e.currentTarget.style.color = '#374151'}
          onMouseLeave={e => e.currentTarget.style.color = '#9ca3af'}
        >
          <FiX size={14} />
        </button>
      </div>
      <div style={{
        height: 2, background: color, transformOrigin: 'left',
        animation: 'ge-bar 4s linear forwards',
      }} />
      <style>{`
        @keyframes ge-in  { from{opacity:0;transform:translateX(14px)} to{opacity:1;transform:translateX(0)} }
        @keyframes ge-out { from{opacity:1;transform:translateX(0)}    to{opacity:0;transform:translateX(14px)} }
        @keyframes ge-bar { from{transform:scaleX(1)} to{transform:scaleX(0)} }
      `}</style>
    </div>
  );
}

function showToast(opts) {
  return new Promise(resolve => {
    const container = document.createElement('div');
    document.body.appendChild(container);
    const dismiss = () => {
      ReactDOM.unmountComponentAtNode(container);
      if (document.body.contains(container)) document.body.removeChild(container);
      resolve(true);
    };
    ReactDOM.render(<Toast {...opts} onDismiss={dismiss} />, container);
  });
}

// ── Modal ─────────────────────────────────────────────────────────────────────

function DialogModal({ title, text, icon = 'info', dangerMode = false, buttons, onResolve }) {
  const cancelText  = typeof buttons?.cancel  === 'string' ? buttons.cancel  : 'Cancelar';
  const confirmText = typeof buttons?.confirm === 'string' ? buttons.confirm : 'OK';
  const showCancel  = buttons && buttons !== true;

  const btnKey   = (dangerMode || icon === 'error') ? 'error' : (BTN_CFG[icon] ? icon : 'default');
  const btnBg    = BTN_CFG[btnKey].bg;
  const btnHover = BTN_CFG[btnKey].hover;

  return (
    <div
      style={{
        position: 'fixed', inset: 0, zIndex: 9999,
        background: 'rgba(0,0,0,0.45)',
        backdropFilter: 'blur(4px)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontFamily: "'AlteHaasGroteskRegular','Segoe UI',sans-serif",
      }}
      onClick={() => onResolve(null)}
    >
      <div
        style={{
          width: 420, background: '#fff', borderRadius: 8,
          boxShadow: '0 20px 60px rgba(0,0,0,0.15), 0 4px 12px rgba(0,0,0,0.06)',
          overflow: 'hidden',
          animation: 'ge-modal-in 0.2s ease-out',
        }}
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '18px 20px 16px', borderBottom: '1px solid #f1f5f9',
        }}>
          <span style={{
            fontWeight: 700, fontSize: 15, color: '#111827',
            fontFamily: "'AlteHaasGroteskBold','Segoe UI',sans-serif",
          }}>
            {title}
          </span>
          <button
            onClick={() => onResolve(null)}
            style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#9ca3af', display: 'flex', padding: 4, borderRadius: 4, transition: 'color 0.15s' }}
            onMouseEnter={e => e.currentTarget.style.color = '#374151'}
            onMouseLeave={e => e.currentTarget.style.color = '#9ca3af'}
          >
            <FiX size={16} />
          </button>
        </div>

        {/* Body */}
        <div style={{ padding: '16px 20px 20px' }}>
          {text && (
            <p style={{ margin: '0 0 20px', color: '#374151', fontSize: 14, lineHeight: 1.65, whiteSpace: 'pre-line' }}>
              {text}
            </p>
          )}

          {/* Buttons */}
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
            {showCancel && (
              <button
                onClick={() => onResolve(false)}
                style={{
                  height: 38, padding: '0 20px', borderRadius: 6, cursor: 'pointer',
                  fontFamily: 'inherit', fontSize: 13.5, fontWeight: 500,
                  border: '1px solid #d1d5db', background: '#fff', color: '#374151',
                  transition: 'background 0.15s',
                }}
                onMouseEnter={e => e.currentTarget.style.background = '#f9fafb'}
                onMouseLeave={e => e.currentTarget.style.background = '#fff'}
              >
                {cancelText}
              </button>
            )}
            <button
              onClick={() => onResolve(true)}
              style={{
                height: 38, padding: '0 20px', borderRadius: 6, cursor: 'pointer',
                fontFamily: 'inherit', fontSize: 13.5, fontWeight: 600,
                border: 'none', background: btnBg, color: '#fff',
                transition: 'background 0.15s',
              }}
              onMouseEnter={e => e.currentTarget.style.background = btnHover}
              onMouseLeave={e => e.currentTarget.style.background = btnBg}
            >
              {confirmText}
            </button>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes ge-modal-in { from{opacity:0;transform:translateY(14px)} to{opacity:1;transform:translateY(0)} }
      `}</style>
    </div>
  );
}

// ── Public API ────────────────────────────────────────────────────────────────

export function showAlert({ title, text, icon, dangerMode, buttons, className } = {}) {
  const hasCancel = buttons && buttons !== true && buttons.cancel;
  if (!hasCancel) return showToast({ title, text, icon, dangerMode });

  return new Promise(resolve => {
    const container = document.createElement('div');
    document.body.appendChild(container);

    const handleResolve = result => {
      ReactDOM.unmountComponentAtNode(container);
      document.body.removeChild(container);
      resolve(result);
    };

    ReactDOM.render(
      <DialogModal
        title={title} text={text} icon={icon}
        dangerMode={dangerMode} buttons={buttons}
        onResolve={handleResolve}
      />,
      container
    );
  });
}
